import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { DividerComponent } from '@shared/components/divider.component';
import { TitleContainerComponent } from '@shared/components/title-container.component';
import { WatchlistsTableComponent } from '../watchlists-table/watchlists-table.component';
import { WatchlistsApiService } from '../services/watchlists.service';
import { CompanyWatchlistTableContent } from '../models/watchlist-company.model';
import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { buildCompanyColumnDefs } from './company-watchlist-table.config';
import { toSignal } from '@angular/core/rxjs-interop';
import { finalize, tap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { CompanyDetailDialogComponent } from './company-detail-dialog/company-detail-dialog.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { getFilenameFromContentDisposition, saveFile } from '@shared/utils/file.utils';

@Component({
  selector: 'app-watchlist-company',
  imports: [
    TitleContainerComponent,
    MatButtonModule,
    MatIconModule,
    DividerComponent,
    WatchlistsTableComponent,
    MatProgressBarModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './company.component.html',
  styleUrl: './company.component.scss',
  providers: [WatchlistsApiService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WatchlistCompanyComponent {
  private readonly dialog = inject(MatDialog);
  readonly watchlistApiService = inject(WatchlistsApiService);

  isLoading = signal<boolean>(true);
  companyColumnDefs = signal<ColDef<CompanyWatchlistTableContent>[]>([]);
  rowData = toSignal(
    this.watchlistApiService.getWatchlistsCompany$().pipe(
      tap(data => {
        this.companyColumnDefs.set(buildCompanyColumnDefs(data.industrySegmentHeaders));
      }),
      finalize(() => {
        this.isLoading.set(false);
      })
    )
  );

  isLoadingExport = signal(false);

  onRowClicked(event: RowClickedEvent<CompanyWatchlistTableContent>): void {
    if (event.data) {
      this.openCompanyDetailDialog(event.data);
    }
  }

  private openCompanyDetailDialog(company: CompanyWatchlistTableContent): void {
    this.dialog.open(CompanyDetailDialogComponent, {
      data: company,
      width: '80vw',
      maxWidth: '900px',
      height: 'auto',
      maxHeight: '90vh',
      disableClose: false,
      autoFocus: true,
    });
  }

  exportExcel(): void {
    this.isLoadingExport.set(true);
    this.watchlistApiService
      .exportWatchlistsCompany$()
      .pipe(
        tap(response => {
          const contentDisposition = response.headers.get('Content-Disposition');
          const filename = getFilenameFromContentDisposition(contentDisposition) ?? 'downloaded-file';
          if (response.body) {
            saveFile(response.body, filename);
          }
        }),
        finalize(() => {
          this.isLoadingExport.set(false);
        })
      )
      .subscribe();
  }
}
